/**
 * InfinitSales - Subscription Service
 * Subscription and plans related API calls
 */
import api from './api';
import requestCache from './requestCache';

const subscriptionService = {
  /**
   * Get current user's subscription status
   *
   * Returns:
   * - has_access: boolean
   * - tier: string | null (e.g., "sales_free", "sales_starter", "sales_pro", "sales_business")
   * - messages_used: number
   * - messages_limit: number
   * - plan_name: string | null
   * - expires_at: string | null
   *
   * @returns {Promise<Object>} Subscription status
   */
  async getSubscriptionStatus({ force = false } = {}) {
    // 5 consumidores independentes (EmpresaDashboard, notificação de criar
    // empresa nos 2 Inbox, AccountPage, PlansPage) disparavam o GET cada um
    // por conta própria — em prod aparecia 3x na mesma carga. TTL curto +
    // dedup em voo: o tier não muda em 60s, e o backend já tem cache de 5min.
    // force: retorno de checkout Stripe (?success) precisa do tier fresco —
    // fura este cache E o de 5 min do backend (refresh=true).
    return requestCache.cachedGet(
      'subscription:status',
      async () => {
        const response = await api.get(
          '/api/subscription/status',
          force ? { params: { refresh: true } } : undefined,
        );
        return response.data;
      },
      { ttl: 60_000, force },
    );
  },

  /**
   * Get available subscription plans
   *
   * Returns array of plans with:
   * - id: string
   * - name: string
   * - slug: string
   * - price_brl: number (price in cents, e.g. 7990 = R$ 79,90)
   * - features: string[]
   * - tokens_limit: number
   * - is_popular: boolean
   * - is_combo: boolean
   *
   * @returns {Promise<Object>} Plans list
   */
  async getPlans() {
    const response = await api.get('/api/subscription/plans');
    return response.data;
  },

  /**
   * Get checkout URL for a specific plan
   * Redirects user to InfinitAgents checkout
   *
   * @param {string} planId - Plan ID to subscribe to
   * @returns {Promise<Object>} Response with checkout_url
   */
  async getCheckoutUrl(planId) {
    const response = await api.get(`/api/subscription/checkout-url?plan_id=${planId}`);
    return response.data;
  },

  /**
   * Get Stripe billing portal URL
   * Opens portal where user can cancel, update payment, view invoices
   *
   * @returns {Promise<Object>} Response with portal_url
   */
  async getPortalUrl() {
    const response = await api.get('/api/subscription/portal-url');
    return response.data;
  },

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Format cents to BRL currency string
   * e.g., 7990 -> "R$ 79,90"
   *
   * @param {number} cents - Price in cents
   * @returns {string} Formatted BRL string
   */
  formatCurrency(cents) {
    if (cents == null || isNaN(cents)) return 'R$ 0,00';
    const reais = Math.floor(cents / 100);
    const centavos = cents % 100;
    return `R$ ${reais.toLocaleString('pt-BR')},${centavos.toString().padStart(2, '0')}`;
  },

  /**
   * Format token count to human-readable string
   * e.g., 1750000 -> "1.75M", 500000 -> "500K", 1000 -> "1K"
   *
   * @param {number} tokens - Token count
   * @returns {string} Formatted token string
   */
  formatTokens(tokens) {
    if (tokens >= 1000000) {
      const millions = tokens / 1000000;
      // Remove trailing zeros
      const formatted = millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(2).replace(/0+$/, '');
      return `${formatted}M`;
    }
    if (tokens >= 1000) {
      const thousands = tokens / 1000;
      const formatted = thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1).replace(/0+$/, '');
      return `${formatted}K`;
    }
    return tokens.toString();
  },
};

export default subscriptionService;
